import { errorWords } from "@alm/domain";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { optionalString, searchPatch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { statusLabel, STATUS_OPTIONS } from "./-components/job-labels";
import { JobTable } from "./-components/job-table";
import type { JobRow } from "@/routes/_authenticated/_workforce/hiring/jobs/-components/job-table-types";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/jobs/")({
  validateSearch: (search: Record<string, unknown>): { status?: string } => ({
    status: optionalString(search.status) || undefined,
  }),
  component: JobsPage,
});

/** Every opening this organization advertises: what is live, what is still a draft. */
function JobsPage() {
  const { facilityId } = useFacility();
  const canManage = useCan("hiring:manage");
  const queryClient = useQueryClient();

  const { status: statusParam } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const statusFilter = statusParam ?? "all";
  const setStatusFilter = (next: string) => patch({ status: next === "all" ? undefined : next });
  const [closing, setClosing] = useState<JobRow | null>(null);

  const list = useQuery(
    orpc.workforce.hiring.listJobs.queryOptions({
      input: { facilityId, status: statusFilter === "all" ? undefined : statusFilter },
    }),
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.workforce.hiring.key() });

  const update = useMutation(
    orpc.workforce.hiring.updateJob.mutationOptions({
      onSuccess: async () => {
        toast.success("Job saved");
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const duplicate = useMutation(
    orpc.workforce.hiring.duplicateJob.mutationOptions({
      onSuccess: async () => {
        toast.success("Job duplicated", {
          description: "The copy is a draft. Edit it before publishing.",
        });
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const close = useMutation(
    orpc.workforce.hiring.closeJob.mutationOptions({
      onSuccess: async (result) => {
        toast.success(result.deleted ? "Posting removed" : "Posting closed");
        setClosing(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = list.data ?? [];
  const activeCount = rows.filter((job) => job.status === "active").length;

  const statusChips = (
    <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0">
      <legend className="sr-only">Status</legend>
      {[{ value: "all", label: "All" }, ...STATUS_OPTIONS].map((option) => (
        <FilterChip
          key={option.value}
          selected={statusFilter === option.value}
          onClick={() => setStatusFilter(option.value)}
        >
          {option.label}
        </FilterChip>
      ))}
    </fieldset>
  );

  return (
    <>
      <PageHeader
        title="Job postings"
        count={list.data ? rows.length : undefined}
        meta={
          list.data
            ? `${activeCount} taking applications, openings, scoped to each facility`
            : "Openings, scoped to each facility."
        }
        actions={
          canManage ? (
            <>
              {/* The builder, not a dialog: a posting is an advert, the questions an applicant
                  answers, the stages they walk and the paperwork at the end, and a box with six
                  fields in it can only ever write the first of those. */}
              <Button asChild>
                <Link to="/hiring/jobs/new">New job</Link>
              </Button>
            </>
          ) : null
        }
      />

      {list.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <ReadFailed
          message="Could not load job postings."
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            statusFilter === "all"
              ? "No job postings yet"
              : `No ${statusLabel(statusFilter).toLowerCase()} postings`
          }
          description={
            statusFilter !== "all"
              ? "Nothing matches this status. Clear the filter to see every posting."
              : facilityId === "all"
                ? "A posting is the advert, the questions applicants answer and the paperwork after the offer."
                : `Other buildings may have postings. Switch building at the top of the page${canManage ? ", or start one with New job" : ""}.`
          }
          action={
            statusFilter !== "all" ? (
              <Button variant="outline" onClick={() => setStatusFilter("all")}>
                Clear filter
              </Button>
            ) : null
          }
        />
      ) : (
        <JobTable
          rows={rows}
          chips={statusChips}
          canManage={canManage}
          duplicatingId={duplicate.isPending ? (duplicate.variables?.jobId ?? null) : null}
          onStatusChange={(job, status) => update.mutate({ jobId: job.id, status })}
          onDuplicate={(job) => duplicate.mutate({ jobId: job.id })}
          onClose={(job) => setClosing(job)}
        />
      )}

      <AlertDialog open={closing !== null} onOpenChange={(open) => !open && setClosing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close {closing?.title ?? "this job posting"}?</AlertDialogTitle>
            <AlertDialogDescription>
              A posting nobody has applied to is removed outright. Once somebody has applied it is
              closed instead, so their application keeps pointing at the job.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep posting</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => closing && close.mutate({ jobId: closing.id })}
            >
              Close posting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
